import { Decimal, dec } from './money.js';
import { InvalidPricingInputError, MissingDimensionsError } from './errors.js';

/**
 * §9.1 — Faz A: formül tabanlı kargo tahmini.
 *
 * Ürün eklenirken anında bir tahmin üretir. Gerçek fiyat depo panelinden çekilene
 * kadar (Faz B, §9.2) bu değer kullanılır ve UI'da "📐 formül" rozetiyle işaretlenir.
 */

export type UnitType = 'LBS' | 'DESI' | 'KG';
export type UnitRounding = 'NONE' | 'CEIL_0_5' | 'CEIL_1';
export type MissingDimsAction = 'USE_FALLBACK' | 'BLOCK_PRODUCT' | 'USE_DIM_ONLY';

/** 1 lb = 0.45359237 kg. Şartname §9.1.2'de 0.4536 kullanılmış; ikisi de kabul edilir. */
const LB_TO_KG = new Decimal('0.4536');

export interface ShippingBand {
  fromUnits: Decimal;
  /** null = üst sınır yok. */
  toUnits: Decimal | null;
  baseFeeUsd: Decimal;
  perUnitFeeUsd: Decimal;
  sortOrder: number;
}

export interface ShippingFormula {
  unitType: UnitType;
  baseFeeUsd: Decimal;
  /** İlk X birim ücrete dahil. */
  includedUnits: Decimal;
  perUnitFeeUsd: Decimal;
  minBillableUnits: Decimal;
  unitRounding: UnitRounding;

  useDimensionalWeight: boolean;
  /** inç³ / lb — uluslararası express standardı 139. */
  dimDivisorIn3PerLb: Decimal;
  /** cm³ / desi — genelde 3000. */
  desiDivisorCm3: Decimal;

  fallbackWeightLbs: Decimal;
  fallbackAction: MissingDimsAction;

  minTotalUsd: Decimal | null;
  maxTotalUsd: Decimal | null;
  surchargeUsd: Decimal;
  /** Yüzde olarak (2 = %2). */
  surchargePct: Decimal;

  bands?: ShippingBand[];
}

export interface ShippingDimensions {
  weightLbs: Decimal | null;
  /** inç */
  lengthIn: Decimal | null;
  widthIn: Decimal | null;
  heightIn: Decimal | null;
  /** cm — DESI modunda kullanılır. */
  lengthCm?: Decimal | null;
  widthCm?: Decimal | null;
  heightCm?: Decimal | null;
}

export interface ShippingEstimate {
  totalUsd: Decimal;
  /** Faturalanan birim (lb veya desi/kg). */
  billableUnits: Decimal;
  /** Gerçek ağırlık mı, hacimsel ağırlık mı belirleyici oldu? */
  basis: 'ACTUAL' | 'DIMENSIONAL' | 'DESI' | 'FALLBACK';
  baseFeeUsd: Decimal;
  perUnitFeeUsd: Decimal;
  chargeableUnits: Decimal;
  bandApplied: boolean;
  clampedBy: 'MIN' | 'MAX' | null;
}

/** §9.1.2 adım 3 — birim yuvarlama. */
export function roundUnits(units: Decimal, mode: UnitRounding): Decimal {
  switch (mode) {
    case 'NONE':
      return units;
    case 'CEIL_0_5':
      // 1.3 → 1.5 · 2.3022 → 2.5 · 2.5 → 2.5
      return units.div('0.5').toDecimalPlaces(0, Decimal.ROUND_CEIL).mul('0.5');
    case 'CEIL_1':
      return units.toDecimalPlaces(0, Decimal.ROUND_CEIL);
    default: {
      const exhaustive: never = mode;
      throw new InvalidPricingInputError(`Bilinmeyen birim yuvarlama modu: ${String(exhaustive)}`);
    }
  }
}

function findBand(formula: ShippingFormula, units: Decimal): ShippingBand | null {
  if (!formula.bands?.length) return null;
  const sorted = [...formula.bands].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    sorted.find(
      (band) => units.gte(band.fromUnits) && (band.toUnits === null || units.lte(band.toUnits)),
    ) ?? null
  );
}

/** §9.1.2 — Kargo ücreti tahmini. */
export function estimateShipping(
  dims: ShippingDimensions,
  formula: ShippingFormula,
): ShippingEstimate {
  if (formula.baseFeeUsd.lt(0) || formula.perUnitFeeUsd.lt(0)) {
    throw new InvalidPricingInputError('Kargo formülünde ücretler negatif olamaz.');
  }
  if (formula.dimDivisorIn3PerLb.lte(0) || formula.desiDivisorCm3.lte(0)) {
    throw new InvalidPricingInputError('Kargo formülünde bölen sıfır veya negatif olamaz.');
  }

  // ── 1) Faturalanabilir ağırlık
  const actual = dims.weightLbs && dims.weightLbs.gt(0) ? dims.weightLbs : null;

  const hasAllDims =
    dims.lengthIn !== null &&
    dims.lengthIn !== undefined &&
    dims.widthIn !== null &&
    dims.widthIn !== undefined &&
    dims.heightIn !== null &&
    dims.heightIn !== undefined &&
    dims.lengthIn.gt(0) &&
    dims.widthIn.gt(0) &&
    dims.heightIn.gt(0);

  const dimWeight =
    formula.useDimensionalWeight && hasAllDims
      ? dims.lengthIn!.mul(dims.widthIn!).mul(dims.heightIn!).div(formula.dimDivisorIn3PerLb)
      : null;

  let billable: Decimal;
  let basis: ShippingEstimate['basis'];

  if (actual !== null && dimWeight !== null) {
    if (dimWeight.gt(actual)) {
      billable = dimWeight;
      basis = 'DIMENSIONAL';
    } else {
      billable = actual;
      basis = 'ACTUAL';
    }
  } else if (actual !== null) {
    billable = actual;
    basis = 'ACTUAL';
  } else if (dimWeight !== null) {
    billable = dimWeight;
    basis = 'DIMENSIONAL';
  } else {
    // §9.1.2 — ne ağırlık ne boyut var
    if (formula.fallbackAction === 'BLOCK_PRODUCT') {
      throw new MissingDimensionsError({ reason: 'Ağırlık ve boyut bilgisi yok.' });
    }
    billable = formula.fallbackWeightLbs;
    basis = 'FALLBACK';
  }

  /*
   * ── 2) Desi modu
   *
   * ⚠️ Bu dal ÜRETİMDE HİÇ ÇALIŞMIYORDU.
   *
   * DESI moduna girmenin tek koşulu cm ölçülerinin dolu olmasıydı. Ama
   * ölçüler sisteme her yerde İNÇ olarak giriyor: SP-API katalogu inç
   * veriyor, `products` tablosunda `source_len_in/wid_in/hei_in` kolonları
   * var, cm kolonu yok. Cm dolduran tek yer ayar ekranının önizlemesiydi
   * (`shipping-formula.service.ts`); üretim yollarının HEPSİ — fiyat
   * hesabı, formül geri-dönüş işi, keşif hattı — yalnızca inç gönderiyordu.
   *
   * Sonuç: kullanıcı formülü DESI'ye ayarlıyor, yazılım sessizce LBS dalını
   * kullanmaya devam ediyordu. Üretimde formül 29 Ağustos'ta DESI yapılmış.
   *
   * Ölçüldü (5 Eylül 2026, üretim verisi, tam ölçülü 16.182 aktif ürün):
   *     kargosu ucuzlayan : 10.156     pahalılaşan : 0
   *     ortalama kargo    : 38,95 USD → 33,52 USD   (−5,42 · −%14)
   * Yani hata TEK YÖNLÜ: maliyeti fazla gösteriyor, fiyatları gereğinden
   * yüksek tutuyordu. Zararına satış üretmiyor ama satış kaybettiriyor.
   *
   * Düzeltme cm'i inçten TÜRETİYOR. Şemaya yeni alan eklemek gerekmiyor ve
   * dört çağıranın dördü birden düzeliyor. Cm açıkça verilmişse o kullanılır.
   */
  const IN_TO_CM = new Decimal('2.54');
  const lenCm = dims.lengthCm ?? (dims.lengthIn ? dims.lengthIn.mul(IN_TO_CM) : null);
  const widCm = dims.widthCm ?? (dims.widthIn ? dims.widthIn.mul(IN_TO_CM) : null);
  const heiCm = dims.heightCm ?? (dims.heightIn ? dims.heightIn.mul(IN_TO_CM) : null);

  const hasCmDims =
    lenCm != null && widCm != null && heiCm != null &&
    lenCm.gt(0) && widCm.gt(0) && heiCm.gt(0);

  if (formula.unitType === 'DESI' && hasCmDims) {
    const desi = lenCm.mul(widCm).mul(heiCm).div(formula.desiDivisorCm3);
    const actualKg = actual ? actual.mul(LB_TO_KG) : new Decimal(0);
    billable = Decimal.max(desi, actualKg);
    basis = 'DESI';
  }

  /*
   * ⚠️ **Birim tutarlılığı.**
   *
   * DESI dalı `billable`'ı KG cinsinden üretiyor; diğer üç dal (gerçek
   * ağırlık, hacimsel ağırlık, yedek ağırlık) LBS üretiyor. Alt sınır,
   * dahil birim ve bant tablosu ise formülün kendi biriminde — yani DESI
   * modunda desi/kg.
   *
   * Karıştırıldığında sonuç ters yönde çalışıyordu: DESI modunda ölçüsü
   * OLMAYAN ürün lbs cinsinden (yedek ağırlık 3 → 3 birim), aynı ağırlıktaki
   * ölçülü ürün kg cinsinden (3 lb = 1,36 kg → 2 birim) ücretleniyordu.
   * Yani veri eksikliği ürünü daha PAHALI gösteriyordu — 26,35 USD'ye karşı
   * 24,35 USD (ADR-223, bağımsız denetim).
   *
   * DESI modunda ağırlıktan gelen her değer kg'a çevriliyor.
   *
   * KG modunda da aynısı: bu dal hiç yoktu, "Kilogram" seçilince faturalanan birim
   * pound kalıyor ve kargo ~2,2 kat pahalı çıkıyordu.
   */
  if (formula.unitType === 'KG' || (formula.unitType === 'DESI' && basis !== 'DESI')) {
    billable = billable.mul(LB_TO_KG);
  }

  // ── 3) Alt sınır ve yuvarlama (sıra §9.1.2'deki gibi)
  billable = Decimal.max(billable, formula.minBillableUnits);
  billable = roundUnits(billable, formula.unitRounding);

  // ── 4) Bant tablosu varsa formülü ezer
  const band = findBand(formula, billable);
  const baseFee = band?.baseFeeUsd ?? formula.baseFeeUsd;
  const perUnit = band?.perUnitFeeUsd ?? formula.perUnitFeeUsd;

  // ── 5) Toplam
  const chargeableUnits = Decimal.max(new Decimal(0), billable.minus(formula.includedUnits));
  let total = baseFee.plus(perUnit.mul(chargeableUnits));
  total = total.mul(new Decimal(1).plus(formula.surchargePct.div(100))).plus(formula.surchargeUsd);

  let clampedBy: ShippingEstimate['clampedBy'] = null;
  if (formula.minTotalUsd !== null && total.lt(formula.minTotalUsd)) {
    total = formula.minTotalUsd;
    clampedBy = 'MIN';
  }
  if (formula.maxTotalUsd !== null && total.gt(formula.maxTotalUsd)) {
    total = formula.maxTotalUsd;
    clampedBy = 'MAX';
  }

  return {
    // Kargo maliyeti aşağı yuvarlanmaz — eksik hesaplanan maliyet kâr yanılsaması yaratır
    totalUsd: total.toDecimalPlaces(2, Decimal.ROUND_UP),
    billableUnits: billable,
    basis,
    baseFeeUsd: baseFee,
    perUnitFeeUsd: perUnit,
    chargeableUnits,
    bandApplied: band !== null,
    clampedBy,
  };
}

/** Yardımcı: `null`/`undefined` güvenli Decimal dönüşümü (DB'den gelen değerler için). */
export function toDim(value: number | string | null | undefined): Decimal | null {
  if (value === null || value === undefined || value === '') return null;
  const d = dec(value);
  return d.gt(0) ? d : null;
}
